import {ChangeDetectorRef, Component, forwardRef, Input, ViewChild} from '@angular/core';
import {AbstractControl, ControlValueAccessor, NG_VALUE_ACCESSOR, NgForm, ValidationErrors} from '@angular/forms';
import {AddressModel, ApplicantModel, KeyIdentifierModel} from '@service/models';
import {debounceTime, distinctUntilChanged, Subject, Subscription} from 'rxjs';
import {PhoneNumberOptionModel} from '@shared/form-components/phone-number/phone-number-option.model';
import {SharedkeyDataService} from '@service/services/common-services/sharedkey-data.service';
import {ApplicantType} from '@service/enum/process-state.enum';
import {ApplicantService} from '@service/services';
import {AppConfigService} from '@service/config/app-config.service';

@Component({
    selector: 'creditsnap-additional-information',
    templateUrl: './additional-information.component.html',
    styleUrls: ['./additional-information.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AdditionalInformationComponent),
            multi: true,
        },
    ],
})
export class AdditionalInformationComponent implements ControlValueAccessor {

    @ViewChild('companyApplicantForm') companyApplicantForm: NgForm;
    @Input('data-test-id') testId: string = 'additional-information';

    private searchSubject = new Subject<string>();
    companyApplicant: ApplicantModel = new ApplicantModel();
    subscription: Subscription;
    keyIdentifier: KeyIdentifierModel;
    companies = [];
    companySearching = false;
    noCompanies = false;
    min3Chars = false;
    maxDate = new Date();
    minDate = new Date();
    phoneInputOptions = new PhoneNumberOptionModel();
    selectedCompany: {id?: string, title?: string, code?: string} = {};
    selectedCompanyTitle: string;
    sameBusinessMailAddress = true;
    businessMailingAddressData: AddressModel = new AddressModel();
    appConfig = AppConfigService.config;

    /** Business products only, and only when the institution-level collectBusinessMailingAddress flag is on */
    get showBusinessMailingAddress(): boolean {
        return !!(this.keyIdentifier?.loanPurposeConfig?.isBusinessProduct && this.appConfig?.collectBusinessMailingAddress);
    }

    constructor(private sharedKeyDataService: SharedkeyDataService,
                private applicantService: ApplicantService,
                private cdr: ChangeDetectorRef) {

        this.phoneInputOptions.required = false;
        this.minDate = new Date(this.minDate.getFullYear() - 500,
            this.minDate.getMonth(),
            this.minDate.getDate());
        this.subscription = this.sharedKeyDataService.keyIdentifier$.subscribe(data => {
            this.keyIdentifier = data;
            this.companyApplicant = this.keyIdentifier.application.applicants.find(item => item.applicantType === ApplicantType.BUSINESS);
            if (this.companyApplicant.dob) {
                this.companyApplicant.dateOfBirth = new Date(this.companyApplicant.dob);
            }
            this.selectedCompanyTitle = this.companyApplicant.companyIndustry || null;
            if (!this.companyApplicant.address) {
                this.companyApplicant.address = new AddressModel();
            }
            this.initBusinessMailingAddress();
        });

        this.searchSubject.pipe(
            debounceTime(300),
            distinctUntilChanged()
        ).subscribe(value => {
            console.log('value >>>>>>>>>>>>>>>', value);
            if (value.length >= 3) {
                this.searchNAICSCompany(value);
            } else {
                this.min3Chars = true;
                this.companies = [];
            }
        });
    }
    private onChange = (value: any) => {};
    private onTouched = () => {};

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    writeValue(obj: any): void {
        if (obj) {
            this.companyApplicant = obj;
            this.cdr.detectChanges();
        }
    }

    notifyChange(): void {
        this.syncBusinessMailingAddress();
        this.onChange(this.companyApplicant);
        this.onTouched();
    }

    /**
     * Restores the saved business mailing address (if any) so a returning applicant sees it
     * instead of the "same as business address" default.
     */
    private initBusinessMailingAddress(): void {
        const saved = (this.companyApplicant?.additionalAddressDetails || [])
            .find(item => item.addressUsage === 'COMMUNICATION');
        this.sameBusinessMailAddress = !(saved && saved.addressId);
        this.businessMailingAddressData = saved ? {...saved} : new AddressModel();
    }

    /** Sets the business applicant's single COMMUNICATION address (copies the business address when "same as" is checked). */
    syncBusinessMailingAddress(applicant: ApplicantModel = this.companyApplicant): void {
        if (!this.showBusinessMailingAddress || !applicant) {
            return;
        }
        const source: AddressModel = this.sameBusinessMailAddress ? applicant.address : this.businessMailingAddressData;
        if (!source) {
            return;
        }
        const details: AddressModel[] = applicant.additionalAddressDetails || [];
        const existing = details.find(item => item.addressUsage === 'COMMUNICATION');
        const businessMailingAddress: AddressModel = {
            streetAddress: source.streetAddress,
            addressLine2: source.addressLine2 || '',
            city: source.city,
            state: source.state,
            zipCode: source.zipCode,
            county: source.county,
            country: source.country,
            addressUsage: 'COMMUNICATION',
            ...(existing?.addressId && {addressId: existing.addressId})
        };
        applicant.additionalAddressDetails = [...details.filter(item => item !== existing), businessMailingAddress];
    }

    /*validate(control: AbstractControl): ValidationErrors | null {
        return null;
    }*/

    runValidation() {
        this.companyApplicantForm.form.markAllAsTouched();
    }


    searchNAICSCompany(q: string) {
        if (q && q.length > 2) {

            this.min3Chars = false;
            this.companies = [];
            this.companySearching = true;
            this.selectedCompany = null;
            this.applicantService.searchNaicsCompanyByName(q).subscribe(data => {
                this.companySearching = false;
                if (data.hits) {
                    this.companies = data.hits.map((item: any) => ({
                        id: item.objectID,
                        title: item.value,
                        code: item.code,
                    }));
                    this.noCompanies = this.companies.length <= 0;
                    this.cdr.detectChanges();
                }
            }, err => {
                this.companySearching = false;
                console.error(err);
            });
        } else if (!q || !q.length) {
            this.min3Chars = false;
            this.companies = [];
            this.noCompanies = true;
        } else if (q && q.length <= 2) {
            this.companies = [];
            this.min3Chars = true;
            this.noCompanies = false;
        }
    }

    selectCompany(_$event: any) {
        if (_$event) {
            const selectedTitle = _$event.option.value;
            // Find company using title from selected option.
            this.selectedCompany = this.companies.find(company => company.title === selectedTitle);
            this.selectedCompanyTitle = this.selectedCompany.title;
            this.companyApplicant.companyIndustry = this.selectedCompany.title;
            this.notifyChange();
        }
    }

    onCompanySearchBlur($event) {
        // Added timeout to not clear input or show validation message for a fraction of moment while selecting option
        setTimeout(() => {
            // Clear the input in case no company selected
            if (!this.selectedCompany) {
                this.selectedCompanyTitle = null;
                this.companyApplicant.companyIndustry = '';
                this.companies = [];
                this.notifyChange();
            }
        }, 200);

    }

    onSearchChange(event, value: string) {
        if (event.key === 'Tab' || event.key === 'Enter') {
            return;
        }
        this.searchSubject.next(value);
    }

    onCompanyInputChange(value: string) {
        this.companyApplicant.companyIndustry = value;
        this.notifyChange();
    }

}
